import React, { useState } from 'react';
import { ArrowLeft, Loader2, CheckCircle, AlertCircle, FileText, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import ImageUploadWithCrop from '../../components/auto-coring/ImageUploadWithCrop';
import ResultEditor from '../../components/auto-coring/ResultEditor';
import { autoCoringService, ScanResult, GradeResult, GradedPart } from '../../services/auto-coring/AutoCoringService';
import { toast } from 'react-hot-toast';
import examData from '../../data/examData.json';
import KaTeXRenderer from '../../components/common/KaTeXRenderer';

const AutoCoringPage = () => {
    const [step, setStep] = useState<'select' | 'upload' | 'scanning' | 'verify' | 'grading' | 'result'>('select');
    const [selectedExam, setSelectedExam] = useState<typeof examData[0] | null>(null);
    const [scannedParts, setScannedParts] = useState<GradedPart[]>([]);
    const [gradeResult, setGradeResult] = useState<GradeResult | null>(null);
    const [submissionImage, setSubmissionImage] = useState<string | null>(null);

    const handleSelectExam = (exam: typeof examData[0]) => {
        setSelectedExam(exam);
        setStep('upload');
    };

    const handleCapture = async (file: File) => {
        if (!selectedExam) return;

        setStep('scanning');
        if (file) {
            setSubmissionImage(URL.createObjectURL(file));
        }
        try {
            const result = await autoCoringService.scanSubmission(file, selectedExam.question);
            setScannedParts(result.parts);
            setStep('verify');
        } catch (error) {
            console.error('Scan error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Có lỗi xảy ra khi quét ảnh';
            toast.error(errorMessage, { duration: 5000 });
            setStep('upload'); // Go back to upload step
        }
    };

    const handleConfirmVerify = async (updatedParts: GradedPart[], options: { penalizeMissingUnit: boolean }) => {
        if (!selectedExam) return;

        setStep('grading');
        try {
            const result = await autoCoringService.gradeSubmission(updatedParts, selectedExam.question, selectedExam.answer, options);
            setGradeResult(result);
            setStep('result');
        } catch (error) {
            console.error(error);
            toast.error('Lỗi khi chấm điểm. Vui lòng thử lại.');
            setStep('verify');
        }
    };

    const handleReset = () => {
        setStep('select');
        setSelectedExam(null);
        setScannedParts([]);
        setGradeResult(null);
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white shadow-sm sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/" className="text-gray-500 hover:text-gray-700 transition-colors">
                            <ArrowLeft size={24} />
                        </Link>
                        <h1 className="text-xl font-bold text-gray-900">Auto Coring AI</h1>
                    </div>
                    <div className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                        OpenAI Grader
                    </div>
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Progress Steps */}
                <div className="flex justify-between items-center mb-8 relative">
                    <div className="absolute left-0 top-1/2 w-full h-1 bg-gray-200 -z-10 rounded-full"></div>
                    {['Chọn Đề', 'Upload', 'AI Scan', 'Verify', 'Kết Quả'].map((s, idx) => {
                        const steps = ['select', 'upload', 'scanning', 'verify', 'grading', 'result'];
                        const currentIdx = steps.indexOf(step);
                        // Map grading to result step for visual simplicity or keep separate
                        const displayIdx = idx;
                        const isActive = idx <= (step === 'grading' ? 3 : steps.indexOf(step) > 3 ? 4 : steps.indexOf(step));

                        return (
                            <div key={s} className={`flex flex-col items-center gap-2 bg-gray-50 px-2`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-500'
                                    }`}>
                                    {idx + 1}
                                </div>
                                <span className={`text-xs font-medium ${isActive ? 'text-blue-700' : 'text-gray-400'}`}>{s}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Content */}
                <div className="bg-white rounded-2xl shadow-sm p-6 min-h-[400px]">

                    {step === 'select' && (
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Chọn đề bài để làm</h2>
                            <div className="grid gap-4">
                                {examData.map(exam => (
                                    <button
                                        key={exam.id}
                                        onClick={() => handleSelectExam(exam)}
                                        className="flex items-start gap-4 p-4 border border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left group"
                                    >
                                        <div className="p-3 bg-blue-100 text-blue-600 rounded-lg group-hover:bg-blue-200 transition-colors">
                                            <FileText size={24} />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900 mb-1">{exam.title}</h3>
                                            <p className="text-sm text-gray-500 line-clamp-2">{exam.question}</p>
                                        </div>
                                    </button>
                                ))}
                                {examData.length === 0 && (
                                    <p className="text-center text-gray-500">Chưa có dữ liệu đề thi. Vui lòng kiểm tra thư mục De thi.</p>
                                )}
                            </div>
                        </div>
                    )}

                    {step === 'upload' && selectedExam && (
                        <div className="text-center py-8">
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">Tải lên bài làm</h2>
                            <p className="text-blue-600 font-medium mb-6">{selectedExam.title}</p>

                            <div className="bg-gray-50 p-4 rounded-lg text-left mb-8 border border-gray-200 max-h-40 overflow-y-auto">
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedExam.question}</p>
                            </div>

                            <ImageUploadWithCrop onImageReady={handleCapture} />
                        </div>
                    )}

                    {step === 'scanning' && (
                        <div className="flex flex-col items-center justify-center py-20">
                            <Loader2 size={48} className="text-blue-600 animate-spin mb-6" />
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">AI đang đọc bài làm...</h3>
                            <p className="text-gray-500">Đang trích xuất nội dung chữ viết tay</p>
                        </div>
                    )}

                    {step === 'verify' && (
                        <div className="animate-fade-in">
                            <h2 className="text-xl font-bold text-gray-800 mb-6">Xác nhận nội dung</h2>
                            <ResultEditor
                                parts={scannedParts}
                                onConfirm={handleConfirmVerify}
                            />
                        </div>
                    )}

                    {step === 'grading' && (
                        <div className="flex flex-col items-center justify-center py-20">
                            <Loader2 size={48} className="text-blue-600 animate-spin mb-6" />
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">AI đang chấm điểm...</h3>
                            <p className="text-gray-500">Đang so sánh bài làm với đáp án chuẩn</p>
                        </div>
                    )}

                    {step === 'result' && gradeResult && (
                        <div className="animate-fade-in">
                            <div className="text-center mb-8">
                                <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${gradeResult.totalScore === gradeResult.totalMaxScore ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'
                                    }`}>
                                    {gradeResult.totalScore === gradeResult.totalMaxScore ? <CheckCircle size={40} /> : <AlertCircle size={40} />}
                                </div>

                                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                                    {gradeResult.totalScore}/{gradeResult.totalMaxScore} Điểm
                                </h2>
                                <p className="text-lg font-medium text-gray-600">
                                    {gradeResult.overallFeedback}
                                </p>
                            </div>

                            <div className="space-y-4 mb-8">
                                {submissionImage && (
                                    <div className="mb-8">
                                        <h3 className="font-bold text-gray-800 mb-3">Ảnh bài làm:</h3>
                                        <div className="rounded-xl overflow-hidden border border-gray-200">
                                            <img src={submissionImage} alt="Bài làm của học sinh" className="w-full h-auto" />
                                        </div>
                                    </div>
                                )}

                                <h3 className="font-bold text-gray-800">Chi tiết chấm điểm:</h3>
                                {gradeResult.parts.map((part, index) => (
                                    <div key={index} className={`p-4 rounded-xl border ${part.isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-2">
                                                {part.isCorrect ? (
                                                    <CheckCircle size={18} className="text-green-600" />
                                                ) : (
                                                    <XCircle size={18} className="text-red-600" />
                                                )}
                                                <span className={`font-semibold ${part.isCorrect ? 'text-green-700' : 'text-red-700'}`}>
                                                    <KaTeXRenderer content={part.feedback || ''} />
                                                </span>
                                            </div>
                                            <span className="font-bold text-gray-900">
                                                {part.score}/{part.maxScore}đ
                                            </span>
                                        </div>
                                        <div className="text-gray-800 font-medium bg-white/50 p-2 rounded">
                                            <KaTeXRenderer content={part.content} />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-gray-50 rounded-xl p-6 text-left mb-8 border border-gray-200">
                                <h4 className="font-semibold text-gray-900 mb-3">Đáp án chi tiết:</h4>
                                <div className="space-y-2 bg-white p-4 rounded-lg border border-gray-200 text-gray-700">
                                    {(selectedExam?.answer || gradeResult.teacherSolution || '').split('\n').map((line, idx) => (
                                        <div key={idx} className="min-h-[1.5em]">
                                            <KaTeXRenderer content={line} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex gap-4 justify-center">
                                <button
                                    onClick={handleReset}
                                    className="btn-primary px-8 py-3 rounded-lg"
                                >
                                    Làm đề khác
                                </button>
                                <Link
                                    to="/"
                                    className="btn-soft px-8 py-3 rounded-lg"
                                >
                                    Về trang chủ
                                </Link>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div >
    );
};

export default AutoCoringPage;
